import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Send, MessageCircle, ArrowLeft, Pencil, Trash2, X, Check } from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";

import BottomNav from "./BottomNav";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";

const authFetch = async (url, options = {}) => {
  const storedToken = localStorage.getItem("session_token");
  const headers = {
    ...options.headers,
    ...(storedToken ? { "Authorization": `Bearer ${storedToken}` } : {}),
  };
  return fetch(url, { ...options, headers });
};

function getFullName(email, name) {
  if (name && name !== email) return name;
  return email.split("@")[0].replace(".", " ").replace(/\b\w/g, c => c.toUpperCase());
}

export default function ChatPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [me, setMe] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState({});
  const [lastMessages, setLastMessages] = useState({});
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [editingMsg, setEditingMsg] = useState(null);
  const [editText, setEditText] = useState("");
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchContacts = async () => {
    try {
      const res = await authFetch(`${API}/chat/contacts`);
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts);
        setMe(data.me);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchUnread = async () => {
    try {
      const res = await authFetch(`${API}/chat/unread`);
      if (res.ok) setUnread(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchLastMessages = async () => {
    try {
      const res = await authFetch(`${API}/chat/last-messages`);
      if (res.ok) setLastMessages(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchMessages = async (contactEmail) => {
    try {
      const res = await authFetch(`${API}/chat/messages/${encodeURIComponent(contactEmail)}`);
      if (res.ok) {
        setMessages(await res.json());
        setUnread(prev => ({ ...prev, [contactEmail]: 0 }));
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchContacts(); fetchUnread(); fetchLastMessages(); }, []);

  useEffect(() => {
    if (!selectedContact) return;
    fetchMessages(selectedContact.email);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      fetchMessages(selectedContact.email);
      fetchUnread();
      fetchLastMessages();
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedContact]);

  const initialScrollDone = useRef(false);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: initialScrollDone.current ? "smooth" : "instant" });
      initialScrollDone.current = true;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim() || !selectedContact) return;
    setSending(true);
    try {
      const res = await authFetch(`${API}/chat/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to_email: selectedContact.email, text: text.trim() }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setText("");
      }
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const selectContact = (contact) => {
    setSelectedContact(contact);
    setMessages([]);
    setEditingMsg(null);
    initialScrollDone.current = false;
  };

  const goBackToContacts = () => {
    setSelectedContact(null);
    setMessages([]);
    setEditingMsg(null);
    initialScrollDone.current = false;
    fetchUnread();
    fetchLastMessages();
  };

  const deleteMessage = async (messageId) => {
    try {
      const res = await authFetch(`${API}/chat/messages/${messageId}`, { method: "DELETE" });
      if (res.ok) {
        setMessages(prev => prev.filter(m => m.message_id !== messageId));
        toast.success("Sõnum kustutatud");
      }
    } catch (e) { console.error(e); }
  };

  const startEdit = (msg) => {
    setEditingMsg(msg.message_id);
    setEditText(msg.text);
  };

  const saveEdit = async (messageId) => {
    if (!editText.trim()) return;
    try {
      const res = await authFetch(`${API}/chat/messages/${messageId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: editText.trim() }),
      });
      if (res.ok) {
        setMessages(prev => prev.map(m => m.message_id === messageId ? { ...m, text: editText.trim(), edited: true } : m));
        setEditingMsg(null);
        toast.success("Sõnum muudetud");
      }
    } catch (e) { console.error(e); }
  };

  return (
    <div className="h-screen bg-slate-50 flex flex-col overflow-hidden">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="md:ml-64 flex flex-col h-full overflow-hidden">
        <header className="bg-white border-b border-slate-200 z-30 flex-shrink-0">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
            <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
            <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" /> MedChat
            </h1>
            {me && <span className="text-sm text-slate-500 ml-auto truncate">{getFullName(me.email, me.name)}</span>}
          </div>
        </header>

      {/* Desktop: side-by-side */}
      <main className="flex-1 max-w-6xl mx-auto w-full p-4 md:p-6 hidden md:flex overflow-hidden">
        <div className="grid grid-cols-[240px_1fr] gap-4 w-full h-full">
          {/* Contact List */}
          <Card className="border-slate-200 flex flex-col overflow-hidden" data-testid="chat-contacts">
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-sm text-slate-500">Kontaktid</CardTitle>
            </CardHeader>
            <CardContent className="p-2 overflow-y-auto flex-1">
              {loading ? (
                <p className="text-center text-slate-400 text-sm p-4">Laadib...</p>
              ) : contacts.length === 0 ? (
                <p className="text-center text-slate-400 text-sm p-4">Kontakte pole</p>
              ) : (
                <div className="space-y-1">
                  {contacts.map(c => {
                    const name = getFullName(c.email, c.name);
                    const isSelected = selectedContact?.email === c.email;
                    const unreadCount = unread[c.email] || 0;
                    const lastMsg = lastMessages[c.email];
                    const isWaiting = unreadCount > 0;
                    return (
                      <button
                        key={c.email}
                        onClick={() => selectContact(c)}
                        className={`w-full text-left px-3 py-3 rounded-lg flex flex-col transition-colors ${
                          isSelected ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"
                        }`}
                        data-testid={`contact-${name}`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className="font-medium text-sm">{name}</span>
                          {unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                              {unreadCount}
                            </span>
                          )}
                        </div>
                        {isWaiting && (
                          <span className={`text-xs mt-0.5 ${isSelected ? "text-red-300" : "text-red-500"} font-medium`}>
                            Kirjutas — ootab vastust
                          </span>
                        )}
                        {lastMsg && !isWaiting && (
                          <span className={`text-xs mt-0.5 truncate ${isSelected ? "text-slate-400" : "text-slate-400"}`}>
                            {lastMsg.from === me?.email ? "Sina: " : ""}{lastMsg.text.slice(0, 30)}{lastMsg.text.length > 30 ? "..." : ""}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Chat Window */}
          <Card className="border-slate-200 flex flex-col overflow-hidden" data-testid="chat-window">
            {!selectedContact ? (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Vali kontakt vestluse alustamiseks</p>
                </div>
              </div>
            ) : (
              <>
                <CardHeader className="pb-2 border-b border-slate-100 flex-shrink-0">
                  <CardTitle className="text-base">
                    {getFullName(selectedContact.email, selectedContact.name)}
                  </CardTitle>
                </CardHeader>
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-3">
                    {messages.length === 0 && (
                      <p className="text-center text-sm text-slate-400 py-8">Vestlus on tühi. Saada esimene sõnum!</p>
                    )}
                    {messages.map((msg) => {
                      const isMe = msg.from_email === me?.email;
                      return (
                        <div key={msg.message_id} className={`group flex ${isMe ? "justify-end" : "justify-start"}`}>
                          {isMe && editingMsg !== msg.message_id && (
                            <div className="flex items-center gap-0.5 mr-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => startEdit(msg)} className="p-1 rounded hover:bg-slate-100" data-testid={`edit-msg-${msg.message_id}`}>
                                <Pencil className="w-3 h-3 text-slate-400" />
                              </button>
                            </div>
                          )}
                          <div
                            className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                              isMe
                                ? "bg-slate-900 text-white rounded-br-md"
                                : "bg-slate-100 text-slate-900 rounded-bl-md"
                            }`}
                            data-testid={`message-${msg.message_id}`}
                          >
                            {editingMsg === msg.message_id ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && saveEdit(msg.message_id)}
                                  className="h-7 text-sm bg-white text-slate-900"
                                  autoFocus
                                />
                                <button onClick={() => saveEdit(msg.message_id)} className="p-1"><Check className="w-4 h-4 text-emerald-400" /></button>
                                <button onClick={() => setEditingMsg(null)} className="p-1"><X className="w-4 h-4 text-slate-400" /></button>
                              </div>
                            ) : (
                              <>
                                <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                                <div className={`flex items-center gap-1.5 mt-1 ${isMe ? "text-slate-400" : "text-slate-500"}`}>
                                  <span className="text-xs">
                                    {new Date(msg.created_at).toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" })}
                                  </span>
                                  {msg.edited && <span className="text-xs italic">muudetud</span>}
                                </div>
                              </>
                            )}
                          </div>
                          {editingMsg !== msg.message_id && (
                            <div className="flex items-center gap-0.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => deleteMessage(msg.message_id)} className="p-1 rounded hover:bg-red-50" data-testid={`delete-msg-${msg.message_id}`}>
                                <Trash2 className="w-3 h-3 text-red-400" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </div>
                <div className="p-3 border-t border-slate-100 flex gap-2 flex-shrink-0">
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Kirjuta sõnum..."
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    disabled={sending}
                    className="text-base"
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!text.trim() || sending}
                    className="bg-slate-900 hover:bg-slate-800 flex-shrink-0"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      </main>

      {/* Mobile: toggle between contacts and chat */}
      <main className="flex-1 p-3 md:hidden flex flex-col overflow-hidden pb-16">
        {!selectedContact ? (
          /* Mobile Contact List */
          <Card className="border-slate-200 flex flex-col flex-1 overflow-hidden" data-testid="chat-contacts-mobile">
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-sm text-slate-500">Kontaktid</CardTitle>
            </CardHeader>
            <CardContent className="p-2 overflow-y-auto flex-1">
              {loading ? (
                <p className="text-center text-slate-400 text-sm p-4">Laadib...</p>
              ) : contacts.length === 0 ? (
                <p className="text-center text-slate-400 text-sm p-4">Kontakte pole</p>
              ) : (
                <div className="space-y-1">
                  {contacts.map(c => {
                    const name = getFullName(c.email, c.name);
                    const unreadCount = unread[c.email] || 0;
                    const lastMsg = lastMessages[c.email];
                    const isWaiting = unreadCount > 0;
                    return (
                      <button
                        key={c.email}
                        onClick={() => selectContact(c)}
                        className="w-full text-left px-3 py-3 rounded-lg flex flex-col hover:bg-slate-100 text-slate-700 transition-colors"
                        data-testid={`mobile-contact-${name}`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className="font-medium text-sm">{name}</span>
                          {unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                              {unreadCount}
                            </span>
                          )}
                        </div>
                        {isWaiting && (
                          <span className="text-xs mt-0.5 text-red-500 font-medium">
                            Kirjutas — ootab vastust
                          </span>
                        )}
                        {lastMsg && !isWaiting && (
                          <span className="text-xs mt-0.5 text-slate-400 truncate">
                            {lastMsg.from === me?.email ? "Sina: " : ""}{lastMsg.text.slice(0, 30)}{lastMsg.text.length > 30 ? "..." : ""}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Mobile Chat Window */
          <Card className="border-slate-200 flex flex-col flex-1 overflow-hidden" data-testid="chat-window-mobile">
            <CardHeader className="pb-2 border-b border-slate-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={goBackToContacts}
                  className="p-1 hover:bg-slate-100 rounded"
                  data-testid="chat-back-btn"
                >
                  <ArrowLeft className="w-5 h-5 text-slate-600" />
                </button>
                <CardTitle className="text-base">
                  {getFullName(selectedContact.email, selectedContact.name)}
                </CardTitle>
              </div>
            </CardHeader>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-3">
                {messages.length === 0 && (
                  <p className="text-center text-sm text-slate-400 py-8">Vestlus on tühi. Saada esimene sõnum!</p>
                )}
                {messages.map((msg) => {
                  const isMe = msg.from_email === me?.email;
                  return (
                    <div key={msg.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                      {isMe && editingMsg !== msg.message_id && (
                        <div className="flex items-center gap-0.5 mr-1">
                          <button onClick={() => startEdit(msg)} className="p-1 rounded hover:bg-slate-100">
                            <Pencil className="w-3 h-3 text-slate-400" />
                          </button>
                        </div>
                      )}
                      <div
                        className={`max-w-[75%] rounded-2xl px-3 py-2 ${
                          isMe
                            ? "bg-slate-900 text-white rounded-br-md"
                            : "bg-slate-100 text-slate-900 rounded-bl-md"
                        }`}
                      >
                        {editingMsg === msg.message_id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && saveEdit(msg.message_id)}
                              className="h-7 text-sm bg-white text-slate-900"
                              autoFocus
                            />
                            <button onClick={() => saveEdit(msg.message_id)} className="p-1"><Check className="w-4 h-4 text-emerald-400" /></button>
                            <button onClick={() => setEditingMsg(null)} className="p-1"><X className="w-4 h-4 text-slate-400" /></button>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                            <div className={`flex items-center gap-1.5 mt-1 ${isMe ? "text-slate-400" : "text-slate-500"}`}>
                              <span className="text-xs">
                                {new Date(msg.created_at).toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" })}
                              </span>
                              {msg.edited && <span className="text-xs italic">muudetud</span>}
                            </div>
                          </>
                        )}
                      </div>
                      {editingMsg !== msg.message_id && (
                        <div className="flex items-center gap-0.5 ml-1">
                          <button onClick={() => deleteMessage(msg.message_id)} className="p-1 rounded hover:bg-red-50">
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>
            <div className="p-3 border-t border-slate-100 flex gap-2 flex-shrink-0">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Kirjuta sõnum..."
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                disabled={sending}
                className="text-base"
                data-testid="chat-input-mobile"
              />
              <Button
                onClick={sendMessage}
                disabled={!text.trim() || sending}
                className="bg-slate-900 hover:bg-slate-800 flex-shrink-0"
                data-testid="send-message-btn-mobile"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        )}
      </main>
      </div>
      <BottomNav />
    </div>
  );
}
